import { inject, Injectable } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import {
  FormControlValidators,
  FormValidators
} from '@core/data-access/models/dynamic-form-field/dynamic-form-field-validators.model';
import {
  CheckboxField,
  CheckboxOption,
  CompanyField,
  DateInputField,
  DropdownField,
  DynamicFormField,
  EmailField,
  FieldKey,
  PasswordField,
  PhoneNumberField,
  RadioField,
  TextInputField,
  ZipCodeField
} from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import {
  isCheckboxField,
  isCompanyField,
  isDateInputField,
  isDropdownField,
  isEmailField,
  isFileUploadField,
  isPasswordField,
  isPhoneNumberField,
  isRadioField,
  isTextInputField,
  isZipCodeField
} from '@core/utils/dynamic-form-field/dynamic-form-field-type-guards';
import { checkboxesRequiredValidator } from '@core/validators/checkboxes-required.validator';
import { emailAddressValidator } from '@core/validators/email-address.validator';
import { matchFieldValidator } from '@core/validators/match-field.validators';
import { phoneNumberValidator } from '@core/validators/phone-number.validator';
import { zipCodeValidator } from '@core/validators/zip-code.validator';
import { Store } from '@ngrx/store';

@Injectable({
  providedIn: 'root'
})
export class DynamicFormFieldValidatorFactory {
  readonly #store = inject(Store);

  createDynamicFieldValidators(
    dynamicFormField: DynamicFormField,
    form: FormGroup,
    formValidators: FormControlValidators
  ) {
    const formControlValidators: FormControlValidators = {
      validators: [],
      asyncValidators: []
    };

    if (isCompanyField(dynamicFormField)) {
      return this.setCompanyFieldValidations(dynamicFormField, formControlValidators);
    }

    if (isZipCodeField(dynamicFormField)) {
      return this.setZipCodeFieldValidations(dynamicFormField, form, formControlValidators);
    }

    if (isPhoneNumberField(dynamicFormField)) {
      return this.setPhoneNumberFieldValidations(dynamicFormField, formControlValidators);
    }

    if (isEmailField(dynamicFormField)) {
      return this.setEmailFieldValidators(dynamicFormField, formValidators, formControlValidators);
    }

    if (isPasswordField(dynamicFormField)) {
      return this.setPasswordFieldValidators(dynamicFormField, formValidators, formControlValidators);
    }

    if (isRadioField(dynamicFormField)) {
      return this.setRadioFieldValidators(dynamicFormField, formControlValidators);
    }

    if (isTextInputField(dynamicFormField)) {
      return this.setTextInputFieldValidators(dynamicFormField, formControlValidators);
    }

    if (isDateInputField(dynamicFormField)) {
      return this.setDateInputFieldValidators(dynamicFormField, formControlValidators);
    }

    if (isFileUploadField(dynamicFormField)) {
      return this.setFileUploadFieldValidators(dynamicFormField, formControlValidators);
    }

    if (isDropdownField(dynamicFormField)) {
      return this.setDropdownFieldValidators(dynamicFormField, formControlValidators);
    }

    if (isCheckboxField(dynamicFormField)) {
      return this.setCheckboxFieldValidators(dynamicFormField, formControlValidators);
    }

    return formControlValidators;
  }

  createCheckboxOptionValidators(isCheckboxFieldHidden: boolean, checkboxOption: CheckboxOption) {
    const { isRequired } = checkboxOption;
    const formControlValidators: FormControlValidators = {
      validators: [],
      asyncValidators: []
    };

    if (isRequired && !isCheckboxFieldHidden) {
      formControlValidators.validators.push(Validators.requiredTrue);
    }

    return formControlValidators;
  }

  private setCompanyFieldValidations(companyField: CompanyField, formControlValidators: FormControlValidators) {
    const { isRequired, maxLengthValidation } = companyField;

    if (isRequired) {
      formControlValidators.validators.push(Validators.required);
    }

    if (maxLengthValidation) {
      formControlValidators.validators.push(Validators.maxLength(maxLengthValidation));
    }

    return formControlValidators;
  }

  private setZipCodeFieldValidations(
    zipCodeField: ZipCodeField,
    form: FormGroup,
    formControlValidators: FormControlValidators
  ) {
    const { isRequired, maxLengthValidation, zipCodeValidation } = zipCodeField;

    if (isRequired) {
      formControlValidators.validators.push(Validators.required);
    }

    if (maxLengthValidation) {
      formControlValidators.validators.push(Validators.maxLength(maxLengthValidation));
    }

    if (zipCodeValidation) {
      formControlValidators.asyncValidators.push(zipCodeValidator(this.#store, form));
    }

    return formControlValidators;
  }

  private setPhoneNumberFieldValidations(
    phoneNumberField: PhoneNumberField,
    formControlValidators: FormControlValidators
  ) {
    const { isRequired, phoneValidation } = phoneNumberField;

    if (isRequired) {
      formControlValidators.validators.push(Validators.required);
    }

    if (phoneValidation) {
      formControlValidators.validators.push(phoneNumberValidator());
    }

    return formControlValidators;
  }

  private setEmailFieldValidators(
    emailField: EmailField,
    formGroupValidators: FormValidators,
    formControlValidators: FormControlValidators
  ) {
    const { isRequired, maxLengthValidation, emailValidation, emailCaseInsensitiveValidation } = emailField;

    if (isRequired) {
      formControlValidators.validators.push(Validators.required);
    }

    if (maxLengthValidation) {
      formControlValidators.validators.push(Validators.maxLength(maxLengthValidation));
    }

    if (emailValidation) {
      formControlValidators.validators.push(emailAddressValidator());
    }

    if (emailField.key === FieldKey.MailConfirm) {
      formGroupValidators.validators.push(
        matchFieldValidator(FieldKey.Email, FieldKey.MailConfirm, emailCaseInsensitiveValidation)
      );
    }

    return formControlValidators;
  }

  private setPasswordFieldValidators(
    passwordField: PasswordField,
    formGroupValidators: FormValidators,
    formControlValidators: FormControlValidators
  ) {
    const { isRequired, minLengthValidation } = passwordField;

    if (isRequired) {
      formControlValidators.validators.push(Validators.required);
    }

    if (minLengthValidation) {
      formControlValidators.validators.push(Validators.minLength(minLengthValidation));
    }

    if (passwordField.key === FieldKey.VerifyPassword) {
      formGroupValidators.validators.push(matchFieldValidator(FieldKey.Password, FieldKey.VerifyPassword));
    }

    return formControlValidators;
  }

  private setRadioFieldValidators(radioField: RadioField, formControlValidators: FormControlValidators) {
    const { isRequired } = radioField;

    if (isRequired) {
      formControlValidators.validators.push(Validators.required);
    }

    return formControlValidators;
  }

  private setTextInputFieldValidators(textInputField: TextInputField, formControlValidators: FormControlValidators) {
    const { isRequired, minLengthValidation, maxLengthValidation } = textInputField;

    if (isRequired) {
      formControlValidators.validators.push(Validators.required);
    }

    if (minLengthValidation) {
      formControlValidators.validators.push(Validators.minLength(minLengthValidation));
    }

    if (maxLengthValidation) {
      formControlValidators.validators.push(Validators.maxLength(maxLengthValidation));
    }

    return formControlValidators;
  }

  private setDateInputFieldValidators(dateInputField: DateInputField, formControlValidators: FormControlValidators) {
    const { isRequired } = dateInputField;

    if (isRequired) {
      formControlValidators.validators.push(Validators.required);
    }

    return formControlValidators;
  }

  private setFileUploadFieldValidators(dateInputField: DateInputField, formControlValidators: FormControlValidators) {
    const { isRequired } = dateInputField;

    if (isRequired) {
      formControlValidators.validators.push(Validators.required);
    }

    return formControlValidators;
  }

  private setDropdownFieldValidators(dropdownField: DropdownField, formControlValidators: FormControlValidators) {
    const { isRequired } = dropdownField;

    if (isRequired) {
      formControlValidators.validators.push(Validators.required);
    }

    return formControlValidators;
  }

  private setCheckboxFieldValidators(checkboxField: CheckboxField, formControlValidators: FormControlValidators) {
    const { isRequired, isHidden } = checkboxField;

    if (isRequired && !isHidden) {
      formControlValidators.validators.push(checkboxesRequiredValidator());
    }

    return formControlValidators;
  }
}
